// Use the workspace-package path for re-exported runtime values below so
// this module stays loadable through Vite's config loader (see
// `../vite-plugin.ts` for the constraint).
import { z } from 'zod';
import {
  DocAudienceSchema,
  DocVisibilitySchema,
  DocContentTypeSchema,
  DocStatusSchema,
} from '@reflex/plugin-docs/shared/page-schema';

export const DocHeadingSchema = z.object({
  depth: z.union([z.literal(2), z.literal(3), z.literal(4)]),
  text: z.string(),
  slug: z.string(),
});
export type DocHeading = z.infer<typeof DocHeadingSchema>;

/**
 * Manifest entry schema. Legacy fields (`slug`, `title`, `group`, `path`,
 * `headings`, `updatedAt`) are required. PRD-3 metadata fields are optional
 * for back-compat with the legacy `buildManifest` helper used by the older
 * markdown-only path; the validated index builder introduced in Workstream
 * A3 always populates them. The `pagePath` field is also added in A3 — it
 * points at the canonical `/docs-content/<slug>.page.json` payload.
 */
export const DocEntrySchema = z.object({
  slug: z.string(),
  title: z.string(),
  group: z.string(),
  path: z.string(),
  pagePath: z.string().optional(),
  headings: z.array(DocHeadingSchema),
  updatedAt: z.string().optional(),
  audience: z.array(DocAudienceSchema).optional(),
  visibility: DocVisibilitySchema.optional(),
  contentType: DocContentTypeSchema.optional(),
  owner: z.string().optional(),
  status: DocStatusSchema.optional(),
  reviewBy: z.string().optional(),
  lastReviewed: z.string().optional(),
  feature: z.string().optional(),
  plugin: z.string().optional(),
  agentType: z.string().optional(),
  summary: z.string().optional(),
  releasedAt: z.string().optional(),
  order: z.number().optional(),
});
export type DocEntry = z.infer<typeof DocEntrySchema>;

export const DocsManifestSchema = z.object({
  generatedAt: z.string(),
  docs: z.array(DocEntrySchema),
});
export type DocsManifest = z.infer<typeof DocsManifestSchema>;

export const DOCS_CONTENT_PREFIX = '/docs-content';
export const DOCS_MANIFEST_PATH = `${DOCS_CONTENT_PREFIX}/manifest.json`;
/** File-extension suffix used for per-page JSON payloads (Workstream A3). */
export const DOC_PAGE_JSON_SUFFIX = '.page.json';

// Re-export the canonical page schema + parsers introduced in Workstream A
// so existing consumers of `plugin-docs/src/shared` see one barrel for both
// the legacy `DocEntry`-style manifest types and the new JSON-block contract.
//
// Workstream A4 mechanically migrates the shipped corpus onto `DocPage`;
// the legacy `DocEntry` manifest survives until A3 lands the structural
// public-only manifest emission.
export {
  DocAudienceSchema,
  DocVisibilitySchema,
  DocContentTypeSchema,
  DocStatusSchema,
  DocReviewBySchema,
  DocLastReviewedSchema,
  DocPageMetadataSchema,
  DocPageSchema,
  DocPageFrontmatterSchema,
  SHIPPED_VISIBILITIES,
} from '@reflex/plugin-docs/shared/page-schema';
export type {
  DocAudience,
  DocVisibility,
  DocContentType,
  DocStatus,
  DocPageMetadata,
  DocPage,
  DocPageFrontmatter,
  DocBlock,
} from '@reflex/plugin-docs/shared/page-schema';
export {
  parseDocPageJson,
  parseDocPageMarkdown,
  splitMarkdownFrontmatter,
} from '@reflex/plugin-docs/shared/parse-page';
export type { ParseDocPageResult } from '@reflex/plugin-docs/shared/parse-page';
